import { apiClient } from './client';

interface RelayStatus {
  active: boolean;
  node?: string;
  epoch?: string;
  rev?: number;
}

interface RelayProvision {
  node: string;
  epoch: string;
  rev: number;
}

interface TraceEntry {
  id: string;
  dir: string;
  amount: number;
  memo: string | null;
  ref: string | null;
  status: string;
  peer: { username: string; displayName: string } | null;
  createdAt: string;
}

interface FluxData {
  capacity: number;
  pending: number;
  traces: TraceEntry[];
}

interface ChallengeData {
  idx: number;
  prevHash: string;
  diff: number;
  reward: number;
  minerId: string;
  payloadHash: string;
  txCount: number;
  nBits?: number;
  timestamp?: number;
  headerVersion: number;
}

interface SealResult {
  idx: number;
  hash: string;
  reward: number;
  diff: number;
  txCount: number;
}

interface ChainInfo {
  height: number;
  lastHash: string | null;
  diff: number;
  reward: number;
  blocks: number;
  pendingTxs: number;
}

interface DistributeResult {
  ok: boolean;
  recipients: number;
  perUser: number;
  actualTotal: number;
}

interface DistributePreview {
  recipients: number;
}

export type { TraceEntry, FluxData, ChallengeData, SealResult, ChainInfo, DistributeResult, DistributePreview };

export const relayApi = {
  status: () => apiClient.get<RelayStatus>('/api/relay/status').then((r) => r.data),
  provision: () => apiClient.post<RelayProvision>('/api/relay/provision').then((r) => r.data),
  extract: () => apiClient.post<{ fragment: string }>('/api/relay/export').then((r) => r.data),
  flux: () => apiClient.get<FluxData>('/api/relay/flux').then((r) => r.data),
  traces: (limit = 30, offset = 0) =>
    apiClient.get<TraceEntry[]>('/api/relay/traces', { params: { limit, offset } }).then((r) => r.data),
  dispatch: (target: string, amount: number, memo?: string) =>
    apiClient.post('/api/relay/dispatch', { target, amount, memo }).then((r) => r.data),
  credit: (target: string, amount: number, memo?: string) =>
    apiClient.post('/api/relay/credit', { target, amount, memo }).then((r) => r.data),
  emit: (target: string, amount: number) =>
    apiClient.post('/api/relay/emit', { target, amount }).then((r) => r.data),
  challenge: () => apiClient.get<ChallengeData>('/api/relay/challenge').then((r) => r.data),
  seal: (nonce: string, hash: string, headerData?: {
    extranonce1: string; extranonce2: string; timestamp: number; nonceUint32: number;
  }) =>
    apiClient.post<SealResult>('/api/relay/seal', { nonce, hash, ...headerData }).then((r) => r.data),
  chain: () => apiClient.get<ChainInfo>('/api/relay/chain').then((r) => r.data),
  distribute: (amount: number, memo?: string) =>
    apiClient.post<DistributeResult>('/api/relay/distribute', { amount, memo }).then((r) => r.data),
  distributePreview: () =>
    apiClient.get<DistributePreview>('/api/relay/distribute/preview').then((r) => r.data),
  mempool: () => apiClient.get('/api/relay/mempool').then((r) => r.data),
};
